import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { v4 as uuid_v4 } from 'uuid';
import { useResolveValue } from '@/hooks';
import { initialQuestionnaireState, questionnaireReducer } from '@/store';
import { InputBlockType, ResolverType } from '@/types';
import { InputBlockTypedAnswerType } from '@/types';
import { buildDependencyRef, flattenBlocks, getBlockTypeName, getLocalStorage, mergeArrays } from '@/utils';
export const QuestionnaireContext = createContext({
    blockList: [],
    mapAnswerExitActions: undefined,
    mapBlockAnswersList: undefined,
    mapBlockInputByScene: undefined,
    mapCachedQueryAnswersByBlock: undefined,
    mapComputedValues: undefined,
    state: initialQuestionnaireState,
    dispatch: () => undefined,
    getAnswersDependency: () => undefined,
    getMediaCore: () => undefined
});
export const useQuestionnaire = () => useContext(QuestionnaireContext);
export const QuestionnaireProvider = ({ children }) => {
    const [state, dispatch] = useReducer(questionnaireReducer, initialQuestionnaireState);
    const { api_url } = getLocalStorage();
    const [resolver] = useResolveValue();
    const mapComputedValues = useMemo(() => {
        const result = new Map();
        state.questionnaire.scenes.forEach((scene) => {
            if (scene.computed_values) {
                const innerMap = new Map();
                scene.computed_values.forEach((cv) => {
                    innerMap.set(cv.computed_value_key, cv);
                });
                result.set(scene.key, innerMap);
            }
        });
        return result;
    }, [state.questionnaire.id]);
    const mapAnswerExitActions = useMemo(() => {
        // Find all the answer conditions for each page by type
        function findKeysByTypes(obj, searchTypes) {
            const results = [];
            function search(obj) {
                if (typeof obj === 'object' && obj !== null) {
                    for (const key in obj) {
                        if (key === 'type' && searchTypes.includes(obj[key])) {
                            results.push(obj);
                        }
                        const value = obj[key];
                        if (typeof value === 'object' && value !== null) {
                            search(value);
                        }
                    }
                }
            }
            search(obj);
            return results;
        }
        const mapping = new Map();
        state.questionnaire.scenes.forEach((scene) => {
            const conditions = [];
            scene.exit_actions.forEach((exitAction) => {
                conditions.push(exitAction.condition);
            }, []);
            conditions.forEach((condition) => {
                const matchedAnswerConditions = findKeysByTypes(condition, [
                    ResolverType.BOOLEAN_IS_ANSWERED_V2,
                    ResolverType.ANSWER_VALUE
                ]);
                matchedAnswerConditions.forEach((answerCondition) => {
                    if ('page_resolver' in answerCondition) {
                        const { type } = answerCondition.page_resolver;
                        let sceneKey = '';
                        if (type === ResolverType.PAGE_BUILDER) {
                            sceneKey = answerCondition.page_resolver.scene_key;
                        }
                        if (type === ResolverType.PAGE_CURRENT) {
                            sceneKey = scene.key;
                        }
                        const answerConditions = mapping.get(sceneKey) || [];
                        if ('answer_key' in answerCondition && 'block_key' in answerCondition) {
                            answerConditions.push({
                                answer_key: answerCondition.answer_key,
                                block_key: answerCondition.block_key
                            });
                        }
                        mapping.set(sceneKey, answerConditions);
                    }
                });
            });
        });
        return mapping;
    }, [state.questionnaire.id]);
    const mapMediaCore = useMemo(() => {
        return Object.fromEntries(state.questionnaire.core_media.map((x) => [x.key, api_url + x.src]));
    }, [state.questionnaire.id]);
    const blockList = useMemo(() => {
        return flattenBlocks([
            ...state.questionnaire.scenes.flatMap((s) => mergeArrays(s.main_blocks, s.footer_blocks)),
            ...state.questionnaire.post_submission_scenes.flatMap((s) => mergeArrays(s.main_blocks, s.footer_blocks))
        ]);
    }, [state.questionnaire.id]);
    const inputBlockList = useMemo(() => {
        return blockList.filter((b) => 'key' in b);
    }, [blockList]);
    const mapSceneKeyByBlockId = useMemo(() => {
        const map = new Map();
        state.questionnaire.scenes.forEach((scene) => {
            flattenBlocks(mergeArrays(scene.main_blocks, scene.footer_blocks)).forEach((block) => {
                map.set(block.id, scene.key);
            });
        });
        return map;
    }, [state.questionnaire.id]);
    const mapBlockInputByScene = useMemo(() => {
        const mapScene = new Map();
        state.questionnaire.scenes.forEach((scene) => {
            const mapBlock = new Map();
            flattenBlocks(mergeArrays(scene.main_blocks, scene.footer_blocks)).forEach((block) => {
                if ('key' in block)
                    mapBlock.set(block.key, block);
            });
            mapScene.set(scene.key, mapBlock);
        });
        return mapScene;
    }, [state.questionnaire.id]);
    const mapCachedQueryAnswersByBlock = useMemo(() => {
        const mapAnswers = new Map();
        inputBlockList.forEach((block) => {
            if (block.type === InputBlockType.INPUT_RADIO_DYNAMIC_CACHED_QUERY) {
                const results = state.cachedQueryAnswers.custom.many[block.result_cache_key];
                const answers = results === null || results === void 0 ? void 0 : results.map((row, i) => {
                    const option = {
                        id: block.answer_id,
                        key: '',
                        label: '',
                        order: i,
                        type: undefined,
                        value: undefined
                    };
                    row.forEach((item) => {
                        const { key, typed_value: { type, value } } = item;
                        if (key === block.answer_key_column)
                            option.key = value;
                        if (key === block.label_column)
                            option.label = value;
                        if (key === block.value_column) {
                            option.value = value;
                            option.type = type;
                        }
                    });
                    return option;
                });
                mapAnswers.set(block.key, answers);
            }
        });
        return mapAnswers;
    }, [inputBlockList]);
    // Map block answers list
    const mapBlockAnswersList = useMemo(() => {
        const getBaseAnswer = (b, a, order = 0) => {
            const scene_key = mapSceneKeyByBlockId.get(b.id);
            let type = getBlockTypeName(b.type);
            if ('type' in a) {
                type = a.type;
            }
            if ('typed_answers' in b) {
                type = getBlockTypeName(b.typed_answers.type, 0);
            }
            return {
                answer_id: a.id,
                answer_key: a.key,
                answered: false,
                block_key: b.key,
                id: uuid_v4(),
                label: typeof a.label === 'string' ? a.label : resolver.resolveString(a.label),
                meta: {
                    order
                },
                page_key: {
                    repeat_index: null,
                    repeat_key: null,
                    scene_key
                },
                typed_value: {
                    // @ts-ignore
                    type,
                    value: a === null || a === void 0 ? void 0 : a.value
                }
            };
        };
        const mapBlocks = new Map();
        inputBlockList.forEach((b) => {
            const mapAnswers = new Map();
            if ('answers' in b) {
                b.answers.forEach((a, order) => {
                    const answer = getBaseAnswer(b, a, order);
                    mapAnswers.set(answer.answer_key, answer);
                });
            }
            if ('typed_answers' in b) {
                b.typed_answers.answers.forEach((a, order) => {
                    const answer = getBaseAnswer(b, a, order);
                    // Previously all answers in config had the same value used in submission. This is not the case anymore
                    // and we need to map and format the different types of answers here.
                    switch (b.typed_answers.type) {
                        case InputBlockTypedAnswerType.DECIMAL: {
                            answer.typed_value.value = {
                                parsed: answer.typed_value.value,
                                raw_value: answer.typed_value.value
                            };
                            break;
                        }
                        default:
                            break;
                    }
                    mapAnswers.set(answer.answer_key, answer);
                });
            }
            if ('answer_yes' in b) {
                const answerYes = getBaseAnswer(b, b.answer_yes);
                const answerNo = getBaseAnswer(b, b.answer_no);
                mapAnswers.set(answerYes.answer_key, answerYes);
                mapAnswers.set(answerNo.answer_key, answerNo);
            }
            if ('answer' in b) {
                const answer = getBaseAnswer(b, b.answer);
                mapAnswers.set(answer.answer_key, answer);
            }
            if ('timezone_answer' in b) {
                const answer = getBaseAnswer(b, b.timezone_answer);
                mapAnswers.set(answer.answer_key, answer);
            }
            if ('utc_answer' in b) {
                const answer = getBaseAnswer(b, b.utc_answer);
                mapAnswers.set(answer.answer_key, answer);
            }
            if (b.type === InputBlockType.INPUT_RADIO_DYNAMIC_CACHED_QUERY) {
                const answers = mapCachedQueryAnswersByBlock.get(b.key) || [];
                answers.forEach((a, order) => {
                    const answer = getBaseAnswer(b, a, order);
                    mapAnswers.set(answer.answer_key, answer);
                });
            }
            mapBlocks.set(b.id, mapAnswers);
        });
        return mapBlocks;
    }, [inputBlockList]);
    /**
     * mapAnswerDependency generates a map of block keys to their dependent AnswerDependency objects.
     *
     */
    const mapAnswerDependency = useMemo(() => {
        const mapDeps = new Map();
        inputBlockList.forEach((block) => {
            var _a;
            const scene_key = mapSceneKeyByBlockId.get(block.id);
            (_a = block === null || block === void 0 ? void 0 : block.dependencies) === null || _a === void 0 ? void 0 : _a.forEach((x) => {
                const dep = Object.assign(Object.assign({}, x), { block_key: block.key, page_resolver: Object.assign(Object.assign({}, x.page_resolver), { scene_key }) });
                const resolvedPage = resolver.resolvePage(x.page_resolver);
                const pKey = buildDependencyRef(x.block_key, resolvedPage);
                const dependencies = mapDeps.get(pKey) || [];
                mapDeps.set(pKey, [...dependencies, dep]);
            });
        });
        return mapDeps;
    }, [inputBlockList]);
    /**
     * Recursively retrieves all answer dependencies for a given block key.
     *
     * Process:
     * 1. Uses a `visited` set to avoid processing the same dependency multiple times and prevent circular references.
     * 2. Retrieves dependencies for the current block key from `mapAnswerDependency` using a unique reference key.
     * 3. Recursively collects dependencies for each dependency found, combining them into a single flat array.
     * 4. Returns the complete list of dependencies.
     */
    const getAnswersDependency = useCallback((blockKey, pageItem, visited = new Set()) => {
        const depRef = buildDependencyRef(blockKey, pageItem);
        if (visited.has(depRef))
            return [];
        visited.add(depRef);
        const dependencies = mapAnswerDependency.get(depRef) || [];
        return dependencies.flatMap((dep) => {
            const childDeps = getAnswersDependency(dep.block_key, pageItem, visited);
            return [dep, ...childDeps];
        });
    }, [mapAnswerDependency]);
    const getMediaCore = useCallback((key) => {
        return mapMediaCore[key] || '';
    }, [mapMediaCore]);
    const value = useMemo(() => ({
        blockList,
        dispatch,
        getAnswersDependency,
        getMediaCore,
        mapAnswerExitActions,
        mapBlockAnswersList,
        mapBlockInputByScene,
        mapCachedQueryAnswersByBlock,
        mapComputedValues,
        state
    }), [state]);
    return _jsx(QuestionnaireContext.Provider, Object.assign({ value: value }, { children: children }));
};
